import React, { createContext, useContext, useState, useCallback } from 'react';
import { en } from './en';
import { nl } from './nl';
import { Translations } from './types';

export type Language = 'en' | 'nl';

const translations: Record<Language, Translations> = { en, nl };

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof Translations, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key as string,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageSt] = useState<Language>(() => {
    const stored = localStorage.getItem('app_language');
    return (stored === 'nl' || stored === 'en') ? stored : 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageSt(lang);
    localStorage.setItem('app_language', lang);
  }, []);

  const t = useCallback((key: keyof Translations, vars?: Record<string, string | number>): string => {
    let str = translations[language][key] ?? translations['en'][key] ?? key as string;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(`{{${k}}}`, String(v));
      });
    }
    return str;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  return useContext(I18nContext);
}
